import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, siteImages } from '../data/mock';
import { Phone, Mail, MapPin, Clock, Send, PawPrint, Building2 } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Contact Paw Supplies Group | Customer Support';
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    setSubmitted(true);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main>
        {/* Banner */}
        <section className="relative h-64 md:h-80 overflow-hidden">
          <img 
            src={siteImages.contactBanner}
            alt="Contact Us"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-brown/85 via-brand-brown/60 to-brand-brown/30" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-brand-cream mb-2">
                <PawPrint className="w-5 h-5" />
                <span className="text-xs font-bold uppercase tracking-[0.25em]">We Are Ready To Help You</span>
              </div>
              <h1 className="text-4xl md:text-5xl text-white font-extrabold mb-3" className-FONT-DISPLAY>
                Contact Us
              </h1>
              <p className="text-brand-cream max-w-lg">
                Have questions? We&apos;d be more than happy to help you and your pet.
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Contact Info Cards */}
            <div className="lg:col-span-1 space-y-6">
              {/* Phone */}
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="block bg-brand-cream border border-brand-divider rounded-2xl p-6 hover:border-brand-orange/40 transition-all group"
              >
                <div className="w-14 h-14 bg-brand-orange-light/30 rounded-xl flex items-center justify-center mb-4 group-hover:bg-brand-orange-light/50 transition-colors">
                  <Phone className="w-6 h-6 text-brand-orange-dark" />
                </div>
                <h3 className="font-bold text-brand-brown text-lg mb-1" className-FONT-DISPLAY>Phone</h3>
                <p className="text-brand-orange-dark font-semibold">{CONTACT_INFO.phone}</p>
                <p className="text-brand-brown-light text-sm mt-2">Mon-Fri 9AM-5PM EST</p>
              </a>

              {/* Email */}
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="block bg-brand-cream border border-brand-divider rounded-2xl p-6 hover:border-brand-orange/40 transition-all group"
              >
                <div className="w-14 h-14 bg-brand-orange-light/30 rounded-xl flex items-center justify-center mb-4 group-hover:bg-brand-orange-light/50 transition-colors">
                  <Mail className="w-6 h-6 text-brand-orange-dark" />
                </div>
                <h3 className="font-bold text-brand-brown text-lg mb-1" className-FONT-DISPLAY>Email</h3>
                <p className="text-brand-orange-dark font-semibold break-all">{CONTACT_INFO.email}</p>
                <p className="text-brand-brown-light text-sm mt-2">We&apos;ll respond within 24 hours</p>
              </a>

              {/* Address */}
              <div className="bg-brand-cream border border-brand-divider rounded-2xl p-6">
                <div className="w-14 h-14 bg-brand-orange-light/30 rounded-xl flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-brand-orange-dark" />
                </div>
                <h3 className="font-bold text-brand-brown text-lg mb-1" className-FONT-DISPLAY>Address</h3>
                <p className="text-brand-brown-light leading-relaxed">
                  {CONTACT_INFO.address.street}<br />
                  {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}<br />
                  {CONTACT_INFO.address.country}
                </p>
              </div>

              {/* Corporate Name Card */}
              <div className="bg-gradient-to-br from-brand-cream to-brand-orange-light/20 border border-brand-divider rounded-2xl p-6">
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm">
                  <Building2 className="w-6 h-6 text-brand-orange-dark" />
                </div>
                <h3 className="font-bold text-brand-brown text-lg mb-1" className-FONT-DISPLAY>Company</h3>
                <p className="text-brand-orange-dark font-semibold text-lg">{CONTACT_INFO.corpName}</p>
              </div>

              {/* Business Hours */}
              <div className="bg-brand-cream border border-brand-divider rounded-2xl p-6">
                <div className="w-14 h-14 bg-brand-orange-light/30 rounded-xl flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-brand-orange-dark" />
                </div>
                <h3 className="font-bold text-brand-brown text-lg mb-3" className-FONT-DISPLAY>Business Hours</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-brand-brown-light">Monday - Friday</span>
                    <span className="text-brand-brown font-medium">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-brand-brown-light">Saturday</span>
                    <span className="text-brand-brown font-medium">10:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-brand-brown-light">Sunday</span>
                    <span className="text-brand-brown-light font-medium">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-brand-cream border border-brand-divider rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-brand-brown mb-2" className-FONT-DISPLAY>Send Us a Message</h2>
                <p className="text-brand-brown-light mb-8">Fill out the form below and we&apos;ll get back to you as soon as possible.</p>
                
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-brand-orange-light/30 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Send className="w-10 h-10 text-brand-orange-dark" />
                    </div>
                    <h3 className="text-2xl font-bold text-brand-brown mb-2">Message Sent!</h3>
                    <p className="text-brand-brown-light mb-6">We&apos;ll get back to you within 24 hours.</p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="px-6 py-3 bg-brand-orange text-white font-semibold rounded-full hover:bg-brand-orange transition-colors"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-brand-brown mb-2">Your Name</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-brand-divider rounded-xl text-brand-brown placeholder:text-brand-brown-light/60 focus:outline-none focus:border-brand-orange transition-colors"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-brand-brown mb-2">Email Address</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-brand-divider rounded-xl text-brand-brown placeholder:text-brand-brown-light/60 focus:outline-none focus:border-brand-orange transition-colors"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-brand-brown mb-2">Subject</label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white border border-brand-divider rounded-xl text-brand-brown focus:outline-none focus:border-brand-orange transition-colors cursor-pointer"
                      >
                        <option value="">Select a subject</option>
                        <option value="order">Order Inquiry</option>
                        <option value="product">Product Question</option>
                        <option value="shipping">Shipping Information</option>
                        <option value="returns">Returns & Refunds</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-brand-brown mb-2">Message</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 bg-white border border-brand-divider rounded-xl text-brand-brown placeholder:text-brand-brown-light/60 focus:outline-none focus:border-brand-orange transition-colors resize-none"
                        placeholder="How can we help you and your pet?"
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-brand-orange to-brand-orange-dark hover:from-brand-orange-dark hover:to-brand-orange text-white font-bold uppercase tracking-wider rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ContactPage;
